/**
 * The face an event was decided for, and what `no_strategy` says
 * about it (CU-869fe6mf4).
 *
 * The event carries `payload.face_id` and nothing else about the face
 * (plan § 1.1): the screen loads the rate-card faces once per page
 * (`setupApi.faces.list()`) and names the axes through the catalogue,
 * the same way Rate cards does. A face the list does not hold — deleted
 * since, or an event written before faces existed — reads `—`.
 *
 * Pure: the sentences are tested without mounting anything, and both
 * the row and the detail read them from here.
 */
// Relative on purpose: `node --test` runs this module without the bundler's aliases.
import { faceLabel as sharedFaceLabel } from '../../Setup/@services/faceLabel.js'

/** `{id: face}` for the faces the Setup API returned. */
export function indexFaces(faces) {
	const out = {}
	for (const f of Array.isArray(faces) ? faces : []) {
		if (f && f.id != null) out[String(f.id)] = f
	}
	return out
}

/**
 * `'Sunny Cars · All tariff types · All insurance'`: the shared rate-card
 * name (`Setup/@services/faceLabel`, UX D1) in full form — site, tariff
 * type and insurance, a blank axis said as "All …" and never `—`. Since
 * CU-869fh646w a blank axis is a choice: the card covers every kind on
 * it and our cheapest row of each cell is the one priced. `sites` is
 * the site catalogue; without it the site is left out. No face → `null`.
 */
export function faceLabel(face, names, sites = null) {
	return sharedFaceLabel(face, sites, names, { elide: false, franchise: false })
}

/** The face of one event, by its payload, or `null`. */
export function faceOf(row, facesById) {
	const id = row?.face_id ?? row?.payload?.face_id
	if (id == null || id === '') return null
	return facesById?.[String(id)] || null
}

/**
 * The two sentences of `no_strategy` (UX decision 7). Which one is
 * decided by whether the event names a face: the engine emits a
 * `no_strategy` without a face when the cell resolves none — nothing
 * covers the cell — and with one when the cell has faces and no
 * strategy matched this one (plan v2.1, item 6 and TL C4). They lead
 * to different actions, so they are two sentences and not one with the
 * face pasted on.
 *
 * `label` is `faceLabel(...)` of the event's face, or `null`.
 */
export function noStrategyCopy(row, label) {
	const hasFace = !!(row?.face_id ?? row?.payload?.face_id)
	if (!hasFace) {
		return {
			reason: 'No strategy covers this office, ACRISS and date, so nothing was priced.',
			detail: null,
		}
	}
	return {
		reason: label
			? `No strategy covers this face — ${label}.`
			: 'No strategy covers this face — a product row that no longer exists (or a rate card of the model before 16 Oct 2026).',
		detail: 'Other strategies cover this cell, but none of them prices a product with this '
			+ 'offer on this site. Add or widen a strategy, or a product row, in Strategies and Setup → Products.',
	}
}
